import type { AppSettings, DailyContentMode, DateMark, DateMarkType, LifeItem, PersistedState } from '../models'

const STORAGE_KEY = 'big-life-calendar:v1'

interface StoredShape {
  version?: number
  items?: Array<Omit<LifeItem, 'completedDates'> & { completedDates?: string[]; completed?: boolean }>
  favoriteQuoteIds?: string[]
  dateMarks?: Array<{ date?: unknown; type?: unknown }>
  settings?: Partial<AppSettings> & { showDailyQuote?: boolean }
}

export const DEFAULT_SETTINGS: AppSettings = {
  fontScale: 1.15,
  showLunar: true,
  dailyContentMode: 'quotes',
  weekStartsSunday: true,
  notificationsEnabled: false,
}

export function loadState(fallbackItems: LifeItem[]): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { version: 5, items: fallbackItems, favoriteQuoteIds: [], dateMarks: [], settings: DEFAULT_SETTINGS }
    return normalizeState(JSON.parse(raw))
  } catch {
    return { version: 5, items: fallbackItems, favoriteQuoteIds: [], dateMarks: [], settings: DEFAULT_SETTINGS }
  }
}

export function normalizeState(value: unknown): PersistedState {
  if (!value || typeof value !== 'object') throw new Error('Unsupported state')
  const parsed = value as StoredShape
  if (!Array.isArray(parsed.items)) throw new Error('Unsupported state')
  const items: LifeItem[] = parsed.items.map(({ completed, ...item }) => ({
    ...item,
    completedDates: Array.isArray(item.completedDates)
      ? item.completedDates
      : completed
        ? [item.date]
        : [],
  }))
  const validMarkTypes = new Set<DateMarkType>(['circle', 'cross', 'star'])
  const dateMarks: DateMark[] = Array.isArray(parsed.dateMarks)
    ? parsed.dateMarks.flatMap((mark) =>
      typeof mark.date === 'string' && validMarkTypes.has(mark.type as DateMarkType)
        ? [{ date: mark.date, type: mark.type as DateMarkType }]
        : [],
    )
    : []
  const storedSettings = parsed.settings ?? {}
  const validDailyContentModes = new Set<DailyContentMode>(['quotes', 'bible', 'off'])
  const dailyContentMode = validDailyContentModes.has(storedSettings.dailyContentMode as DailyContentMode)
    ? storedSettings.dailyContentMode as DailyContentMode
    : storedSettings.showDailyQuote === false ? 'off' : 'quotes'
  const currentSettings = { ...storedSettings }
  delete currentSettings.showDailyQuote
  return {
    version: 5,
    items,
    favoriteQuoteIds: Array.isArray(parsed.favoriteQuoteIds)
      ? parsed.favoriteQuoteIds.filter((id): id is string => typeof id === 'string')
      : [],
    dateMarks,
    settings: { ...DEFAULT_SETTINGS, ...currentSettings, dailyContentMode },
  }
}

export function saveState(state: PersistedState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
}
